import { getTradingOrderDetailsRoute } from '@src/containers/Routes/routes';
import { useRoleAuth } from '@src/hooks/useRoleAuth';
import type { CareOrder, FilterableProperty, UseFilterBuilderOutput } from '@talos/kyoko';
import {
  ACTION,
  filterByCellValueMenuItem,
  getShowJSONContextItem,
  IconName,
  Order,
  Quote,
  useCallbackRef,
  useDisclosureWithContext,
  useGetDefaultContextMenuItems,
  useJsonModal,
  useMixpanel,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, IRowNode, MenuItemDef } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo, type ReactNode } from 'react';
import { selectCurrentLayout } from '../../../components/AppLayout/AppLayoutSlice';
import { DEFAULT_LAYOUT_ID } from '../../../components/AppLayout/Layouts/tokens';
import { useFeatureFlag } from '../../../hooks';
import { useAppStateSelector } from '../../../providers/AppStateProvider';
import { useOrderMenu } from '../Orders/useOrderMenu';
import { getCopyFundQuotePricesItem } from '../Quotes/MenuItems/copyFundQuotePricesItem';
import { useQuotesMenu } from '../Quotes/useQuotesMenu';
import { ModifyFixingDialog, type ModifyFixingProps } from './Dialogs/ModifyFixingDialog';
import type { CareOrderBlotterEntity } from './types';
import { isCareOrderRow, isOrderRow, isQuoteRow, isQuoteRowWithOrder, LegRow, QuoteRow } from './types';
import { useCareOrderActionMenuItems } from './useCareOrderActionMenuItems';
import { colIDToFilterBuilderKey } from './useCareOrderFilter';
import { useModifyNettingMenuItem } from './useModifyNettingMenuItem';
import type { useNetCareOrdersMenuItem } from './useNetCareOrdersMenuItem';

interface UseCareOrderMenuParams {
  filterableProperties: FilterableProperty[];
  openClause: UseFilterBuilderOutput['addAndOpenClause'];
  onSendRFQ: (careOrder: CareOrder) => void;
  netCareOrdersMenu: ReturnType<typeof useNetCareOrdersMenuItem<CareOrder>>;
}

export function useCareOrderMenu({
  filterableProperties,
  openClause,
  onSendRFQ,
  netCareOrdersMenu,
}: UseCareOrderMenuParams): {
  getContextMenuItems: (params: GetContextMenuItemsParams) => (MenuItemDef | string)[];
  dialogs: ReactNode;
} {
  const mixpanel = useMixpanel();
  const { handleClickJson, jsonModal } = useJsonModal();
  const getDefaultContextMenuItems = useGetDefaultContextMenuItems();
  const layoutId = useAppStateSelector(selectCurrentLayout)?.id ?? DEFAULT_LAYOUT_ID;
  const { getItem: getModifyFixingItem, dialogComponent: modifyFixingDialogComponent } =
    useModifyFixingContextMenuItem();
  const modifyNettingMenu = useModifyNettingMenuItem<CareOrder>();
  const careOrderActionMenuItems = useCareOrderActionMenuItems();

  const { enableCareOrderNetting } = useFeatureFlag();

  const orderMenu = useOrderMenu({
    generateOrderDetailsRoute: getTradingOrderDetailsRoute,
    layoutId,
  });
  const quoteMenu = useQuotesMenu();

  const getContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<CareOrderBlotterEntity>) => {
    const entity = params.node?.data;
    const selectedCareOrders = getSelectedCareOrderRows(params);
    const netCareOrdersItem = enableCareOrderNetting
      ? netCareOrdersMenu.getMenuItem({
          selectedCareOrders,
        })
      : null;
    const modifyNettingItem = enableCareOrderNetting
      ? modifyNettingMenu.getMenuItem({
          contextCareOrder: entity && isCareOrderRow(entity) ? entity : undefined,
          selectedCareOrders,
        })
      : null;

    if (isGetContextMenuItemsParamsOrder(params)) {
      return orderMenu.getContextMenuItems(params);
    }

    if (entity instanceof QuoteRow) {
      const quoteMenuItems: (MenuItemDef | string)[] = isGetContextMenuItemsParamsQuote(params)
        ? quoteMenu.getContextMenuItems(params)
        : [];

      const copyPricingItem = getCopyFundQuotePricesItem(getSelectedQuotesWithParentCOs(params));
      if (copyPricingItem) {
        quoteMenuItems.unshift('separator');
        quoteMenuItems.unshift(copyPricingItem);
      }

      const modifyFixingItem = getModifyFixingItem(params);
      if (modifyFixingItem) {
        quoteMenuItems.unshift('separator');
        quoteMenuItems.unshift(modifyFixingItem);
      }

      return quoteMenuItems;
    }

    if (entity instanceof LegRow) {
      return compact([
        getShowJSONContextItem({ params, handleClickJson }),
        'separator',
        ...getDefaultContextMenuItems(params),
      ]);
    }

    if (!entity || !isCareOrderRow(entity)) {
      return getDefaultContextMenuItems(params);
    }

    // Now we know that entity is a CareOrder
    let isCancelable: boolean = entity.isCancelable;
    if (params.node?.childrenAfterFilter != null) {
      for (const row of params.node.childrenAfterFilter) {
        if (isQuoteRow(row.data) && !row.data.isTerminal) {
          isCancelable = false;
          break;
        }
        if (isOrderRow(row.data) && !row.data.isComplete) {
          isCancelable = false;
          break;
        }
      }
    }

    const items = [
      ...filterByCellValueMenuItem({
        params,
        filterableProperties,
        openClause,
        colIDToFilterBuilderKey: colIDToFilterBuilderKey,
        mixpanel,
      }),
      'separator',
      {
        icon: `<i class="ag-icon ${IconName.BookOpen}"/>`,
        disabled: !entity.isTradable,
        name: 'Send RFQ',
        action: () => onSendRFQ(entity),
      },
      netCareOrdersItem,
      modifyNettingItem,
      careOrderActionMenuItems.getModifyMenuItem(entity),
      careOrderActionMenuItems.getCancelMenuItem({
        entity,
        isCancelable,
      }),
      entity ? getShowJSONContextItem({ params, handleClickJson }) : undefined,
      careOrderActionMenuItems.getForceCancelMenuItem(entity),
    ];

    return compact([...items, ...getDefaultContextMenuItems(params)]);
  });

  const dialogs = useMemo(
    () => (
      <>
        {jsonModal}
        {orderMenu.dialogComponents}
        {quoteMenu.dialogs}
        {modifyFixingDialogComponent}
        {netCareOrdersMenu.dialogs}
        {modifyNettingMenu.dialogs}
      </>
    ),
    [
      jsonModal,
      orderMenu.dialogComponents,
      quoteMenu.dialogs,
      modifyFixingDialogComponent,
      netCareOrdersMenu.dialogs,
      modifyNettingMenu.dialogs,
    ]
  );

  return {
    getContextMenuItems,
    dialogs,
  };
}

/** Pulls some strings to put together and prepare the ModifyFixingDialog and a getter to get the context menu item for you */
export const useModifyFixingContextMenuItem = () => {
  const { isAuthorized } = useRoleAuth();
  const { enableTakerModifyFixing, enableModifyFixingDialogV2 } = useFeatureFlag();
  const allowTakerModifyFixing = enableTakerModifyFixing && isAuthorized(ACTION.EDIT_ORDER_FIXING_DETAILS);
  const modifyFixingDisclosure = useDisclosureWithContext<ModifyFixingProps>();

  const getItem = useCallbackRef((params: GetContextMenuItemsParams<CareOrderBlotterEntity>) => {
    const entity = params.node?.data;
    if (!entity || !isQuoteRowWithOrder(entity)) {
      return undefined;
    }

    const showModifyFixingItem =
      parentIsDoneForDayCareOrder(params.node) &&
      allowTakerModifyFixing &&
      entity.PricingMode === 'SpreadToFixing' &&
      entity.OrdStatus === 'DoneForDay' &&
      entity.FixingDetails != null;
    if (showModifyFixingItem) {
      return {
        name: 'Modify Fixing',
        action: () => modifyFixingDisclosure.open({ quote: entity, orderID: entity.OrderID, mode: 'taker' }),
        icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
        disabled: entity.isMultitoken && !enableModifyFixingDialogV2,
      };
    }

    return undefined;
  });

  const dialogComponent = <ModifyFixingDialog {...modifyFixingDisclosure} />;

  return {
    getItem,
    dialogComponent,
  };
};

function isGetContextMenuItemsParamsOrder(
  params: GetContextMenuItemsParams
): params is GetContextMenuItemsParams<Order> {
  return params.node != null && params.node.data instanceof Order;
}

function isGetContextMenuItemsParamsQuote(
  params: GetContextMenuItemsParams
): params is GetContextMenuItemsParams<Quote> {
  return params.node != null && params.node.data instanceof Quote;
}

function parentIsDoneForDayCareOrder(node: IRowNode<any> | null) {
  const parent = node?.parent?.data;
  if (!isCareOrderRow(parent)) {
    return false;
  }

  return parent.OrdStatus === 'DoneForDay';
}

// Maps the params object form our blotter to the expected type of the StfQuotePrice menu item
function getSelectedQuotesWithParentCOs(
  params: GetContextMenuItemsParams<CareOrderBlotterEntity>
): Parameters<typeof getCopyFundQuotePricesItem>[0] {
  const selectedQuoteNodes = params.api
    .getSelectedNodes()
    .filter((node): node is IRowNode<QuoteRow> => isQuoteRow(node.data));

  return selectedQuoteNodes.map(quoteNode => {
    const parentCO = isCareOrderRow(quoteNode.parent?.data) ? quoteNode.parent.data : undefined;
    return { quote: quoteNode.data, parentCO };
  });
}

function getSelectedCareOrderRows(params: GetContextMenuItemsParams<CareOrderBlotterEntity>): CareOrder[] {
  return getSelectedCareOrderRowsFromNodes(params.api.getSelectedNodes());
}

export function getSelectedCareOrderRowsFromNodes(nodes: IRowNode<CareOrderBlotterEntity>[]): CareOrder[] {
  const isCareOrderNode = (
    node: IRowNode<CareOrderBlotterEntity>
  ): node is IRowNode<CareOrderBlotterEntity> & { data: CareOrder } => isCareOrderRow(node.data);

  return nodes.filter(isCareOrderNode).map(node => node.data);
}
